'use client';

import classNames from 'classnames';
import { useRef, useState } from 'react';

type TooltipProps = React.HTMLAttributes<HTMLElement> & {
    content?: React.ReactNode;
    position?: 'top' | 'bottom' | 'left' | 'right' | 'bottom-right';
    trigger?: 'hover' | 'click';
    delay?: number;
};

const Tooltip = ({
    content,
    position = 'top',
    trigger = 'hover',
    delay = 200,
    className = '',
    children,
    ...props
}: TooltipProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const showTooltip = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        const id = setTimeout(() => {
            setIsVisible(true);
        }, delay);
        setTimeoutId(id);
    };

    const hideTooltip = () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
        setIsVisible(false);
    };

    const toggleTooltip = () => {
        if (isVisible) {
            hideTooltip();
        } else {
            showTooltip();
        }
    };

    const handleMouseEnter = () => {
        if (trigger === 'hover') {
            showTooltip();
        }
    };

    const handleMouseLeave = () => {
        if (trigger === 'hover') {
            hideTooltip();
        }
    };

    const handleClick = () => {
        if (trigger === 'click') {
            toggleTooltip();
        }
    };

    if (!content) return children;

    return (
        <div
            {...props}
            className={`tooltip-v2 ${className}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
            ref={tooltipRef}
        >
            {children}
            <div
                className={classNames(`tooltip-v2__content tooltip-v2__content--${position}`, {
                    'tooltip-v2__content--visible': isVisible,
                })}
            >
                {content}
            </div>
        </div>
    );
};

export default Tooltip;
